import React, { useState } from 'react'

import { Box, Button, Stack, Typography } from '@mui/material'
import { ArrowLeft, Tag } from 'lucide-react'

import CurrencyField from 'components/UI/CurrencyField'
import { ToggleRow } from 'components/UI/ToggleRow'
import { ProductCard } from 'components/productCard'
import { font, space } from 'settings/tokens'
import { glassSx } from 'utils/glass'

import type { CertificateCustomer } from 'types'

/**
 * VERA-637 — set an ASKING PRICE, so the public certificate can be bought.
 *
 * ## This copy is DEMO-TRIMMED; `agent/VERA-632-App.Sell` holds the real one
 *
 * Brought onto `main` ahead of VERA-632 so the sell flow can be clicked through in front of
 * someone, without the listing and escrow endpoints existing here. The ONLY divergence from
 * 632's file is the CTA: `useSetSaleListing` / `useRemoveSaleListing` became the `onSubmit` /
 * `onRemove` props, because those endpoints are not on `main` and calling them would 404 mid-demo.
 * The field, its validation, the toggle and every line of copy are 632's, unchanged.
 *
 * When 632 lands this file will conflict. **Take 632's version** — it is the authority, and its
 * hooks are the point. Do not try to merge the two.
 *
 * A step of the sell sheet, rendered by `SellCertificate` when the seller picks "List for sale"
 * (§52 — the sheet swaps its body rather than opening a third drawer). Not a drawer itself.
 *
 * ## What this deliberately is not
 *
 * There is no recipient, no sale mode and no confirm scan, because none of them has anything to
 * decide yet. Listing creates no transfer and moves no money — it writes a price and puts a
 * purchase affordance on the public page. Whoever buys goes through the offer flow afterwards,
 * and that is where the recipient, the escrow window and the possession scan all belong.
 *
 * Same reason there is no "Set up payouts" gate here, unlike the offer sheet: D5 requires a
 * verified account to ACCEPT a sale, not to list one. Demanding Stripe onboarding before an owner
 * may type a number would gate the cheap half of the feature on the expensive half.
 *
 * ## Two controls, one of which is not a price
 *
 * The visibility toggle is not decoration. The public certificate withholds the listing unless the
 * view says otherwise, and the server fails CLOSED, so a price published with the switch off
 * changes nothing on the page the owner just listed. Putting the switch beside the price is what
 * stops that being a support ticket.
 *
 * It now OPENS ON for a new listing (see `visibilityChoice`), which turns that from a trap into a
 * choice: the default matches what someone typing an asking price is almost always asking for,
 * and the switch is there for the minority who want the price shown only to people they share
 * the certificate with. The stored value still wins when a listing already exists.
 */

const PRICE_ERROR = 'The price must be greater than $0. Please enter a valid amount.'

/**
 * Who can see the asking price, said in the words of whichever state the switch is in.
 *
 * Both halves are needed because the OFF state is not "nobody" — a listing withheld from the
 * public certificate is still served on any link the owner has shared that publishes it. The
 * single unconditional sentence this replaces ("Anyone who opens this certificate...") was
 * therefore a statement that was only true half the time, and false in precisely the state an
 * owner turns the switch off to reach.
 *
 * Exported so the certificate page's own listing card renders the same two sentences (§44 — one
 * fact, one wording, every surface reads it). They had already drifted once: the card said "can
 * see the price and buy it" where this row said "sees the price and can buy it".
 */
export const saleListingAudienceCopy = (isPubliclyVisible: boolean) =>
  isPubliclyVisible
    ? 'Anyone who opens this certificate can see the price and buy it.'
    : 'This listing is private. Only people you share this certificate with can see the price and buy it.'

interface ListForSaleProps {
  certificate: CertificateCustomer
  /** Whether the certificate already carries a listing, from the certificate payload. */
  isListed: boolean
  /** The current asking price in MAJOR units, so the field opens on what was set. */
  currentPrice?: number
  /**
   * The STORED `show_sale_listing` on the public view.
   *
   * What the switch RENDERS is not this directly — a new listing opens ON regardless, see
   * `visibilityChoice`. This is the value that decides whether the step owes the server a write
   * when the owner submits without touching the switch.
   */
  isPubliclyVisible: boolean
  onVisibilityChange: (next: boolean) => void
  onBack: () => void
  /**
   * Publish the asking price.
   *
   * On VERA-632 this component called `useSetSaleListing` itself. It takes the write as a prop
   * on `main` because the endpoint does not exist here — the demo path hands back a step saying
   * so rather than firing a request that would 404. Everything above the CTA (the field, the
   * validation, the toggle and its copy) is 632's, unchanged.
   */
  onSubmit: (price: number, isPubliclyVisible: boolean) => void
  /** Clear the listing. Only reachable when `isListed`, same as on 632. */
  onRemove?: () => void
  /** True while the parent's write is in flight; disables both buttons (§46). */
  isBusy?: boolean
}

export const ListForSale = ({
  certificate,
  isListed,
  currentPrice,
  isPubliclyVisible,
  onVisibilityChange,
  onBack,
  onSubmit,
  onRemove,
  isBusy = false,
}: ListForSaleProps) => {
  const [price, setPrice] = useState<number | undefined>(currentPrice)
  /*
    Has the user PRESSED the button with a price the server would refuse?

    Gates the DISPLAY of the error only — `priceInvalid` below is still computed on every
    keystroke and is still what refuses the submit, exactly as `addressBlurred` gates the
    recipient picker's undeliverable message without touching the validation behind it.

    ## Why the trigger is the press, and what it cost to make that possible

    Two earlier gates were both wrong in the same direction: they accused the user of a mistake
    before they had made one. `priceTouched` (set in the field's `onBlur`) reddened an empty
    field whenever focus left it, so merely tapping the price and then tapping elsewhere was
    enough. `priceDirty` (set on the first keystroke) was narrower but still fired mid-entry —
    it is armed the instant a "1" is typed, and every price passes through a moment of being
    incomplete.

    The press is the only unambiguous "I am done" signal this surface has, and unlike the
    recipient picker it HAS a press to use — but only after removing the price from the CTA's
    disabled condition. That is the deliberate trade: the button used to be dead in exactly the
    state the message describes, so the message was unreachable and the user got a dead control
    with no explanation. A live button that names the problem when pressed beats a dead one that
    names nothing. The submit still refuses (see the guard in `onClick`), so nothing invalid is
    ever sent.

    NOT cleared on the next keystroke, which is where this deliberately diverges from
    `addressBlurred`. That flag clears on input because blur will re-arm it a moment later; this
    one has no second trigger, so clearing it would take the message down and leave it down until
    another press. Staying armed is what makes correcting the price clear the error and emptying
    it again bring the error back.
  */
  const [priceSubmitted, setPriceSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  /*
    VERA-632 review — the switch opens ON for a NEW listing.

    `isPubliclyVisible` is the PERSISTED `show_sale_listing` on the public view, and its stored
    default is false (`DEFAULT_CERTIFICATE_VIEW_VISIBILITY` in `@repo/types`) because the server
    must fail closed: a link written before the key existed cannot start advertising a price on
    the day the toggle ships. That is the right default for a certificate nobody has listed. It
    is the wrong default for the act of listing one — an owner typing an asking price is asking
    to be bought from, and opening the switch OFF made the common intention the one that needed
    an extra tap, on a control they had to notice first.

    The stored value still wins for an EXISTING listing, so re-opening the step never clobbers a
    decision already made. `null` means "untouched", which is what lets the two cases be told
    apart at submit time below.
  */
  const [visibilityChoice, setVisibilityChoice] = useState<boolean | null>(null)
  const isVisible = visibilityChoice ?? (isListed ? isPubliclyVisible : true)

  const priceInvalid = price === undefined || price <= 0
  const showPriceError = priceSubmitted && priceInvalid

  /*
    VERA-632 review — NO `minHeight`. The sheet sizes to the step it is showing.

    It used to force 500px while the content Stack took `flex: 1`, which pushed the CTA
    to the floor of that 500 regardless of how short the step was. On the listing step —
    a product row, one price field and one toggle, about 330px including the header —
    that left ~170px of dead space between the last control and the button, which is
    what review reported twice (the listing step and the possession step).

    §52's precedent for a FIXED height is not this case, and the distinction matters:
    the recipient picker is pinned at 75vh because its OWN content changes while you
    stay on it (results appear and disappear as you type), so it would resize under the
    reader's hands — "really jumpy and odd". These are STEPS. Moving between them is a
    navigation, and a new screen being a different size is what navigation looks like
    everywhere else in the app.

    `DrawerPopup` already caps a bottom sheet at 90vh with `height: auto`, so removing
    the floor gives up nothing at the tall end.
  */
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/*
        A back arrow, not a second Cancel — §52: when level one becomes a step machine the header
        affordance changes with the step, and a second dismissal competing with the sheet's own X
        is exactly the ambiguity stacking drawers creates.
      */}
      <Stack
        direction='row'
        alignItems='center'
        onClick={onBack}
        role='button'
        tabIndex={0}
        sx={{ gap: `${space.sm}px`, cursor: 'pointer', marginBottom: `${space.lg}px` }}
      >
        <ArrowLeft size={18} strokeWidth={1.5} />
        <Typography sx={{ fontSize: `${font.label}px` }}>Back</Typography>
      </Stack>

      {/* One Stack owns every gap. Each section below is a bare Box and a DIRECT child — a local
          `mb` on a direct child is silently killed by MUI's Stack spacing rule. */}
      <Stack direction='column' spacing={3} sx={{ flex: 1, pb: 2 }}>
        <Box>
          <ProductCard product={certificate.product as any} bare />
        </Box>

        <Box>
          <CurrencyField
            label='Asking price'
            value={price}
            onChange={setPrice}
            error={showPriceError ? PRICE_ERROR : undefined}
          />
        </Box>

        <Box>
          <ToggleRow
            label='Show on the public certificate'
            /*
              The description states the CURRENT audience rather than only what turning it on
              would do, because the off state is the one an owner can misread. "Turn it off to
              keep the listing to yourself" — the sentence this replaces — was simply untrue:
              a withheld listing is not private to the owner, it is private to the people they
              have shared the certificate with. The label already says what the switch does, so
              the sentence is free to say who is looking.
            */
            description={saleListingAudienceCopy(isVisible)}
            checked={isVisible}
            onChange={next => {
              setVisibilityChoice(next)
              onVisibilityChange(next)
            }}
          />
        </Box>
      </Stack>

      {/*
        Pinned OUTSIDE the Stack, matching the gift and sell sheets. Those files record the
        measurement that forced it: the button otherwise sat at 771-815px in an 812px viewport,
        i.e. off the bottom of the phone this is designed for.
      */}
      <Box
        sx={theme => ({
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
          ...glassSx(theme),
          pt: 2,
          pb: 1,
        })}
      >
        {submitError && (
          <Typography
            sx={theme => ({
              fontSize: `${font.label}px`,
              color: theme.palette.error.main,
              lineHeight: 1.5,
              marginBottom: `${space.md}px`,
            })}
          >
            {submitError}
          </Typography>
        )}

        <Button
          fullWidth
          variant='contained'
          /*
            NOT `priceInvalid || isBusy`. The price is deliberately out of the disabled
            condition — see `priceSubmitted`: a button that is dead in exactly the state the
            error describes can never show the error, so the message was unreachable. It stays
            disabled while a request is in flight, which is a different case (§46: an in-flight
            mutation disables, and re-pressing would race the one already running).
          */
          disabled={isBusy}
          onClick={() => {
            /*
              The press is both the submit and the moment the error becomes showable. Arming
              first, then refusing, is what puts the message on screen for the user who pressed
              with nothing typed.
            */
            setPriceSubmitted(true)
            if (priceInvalid || price === undefined) return
            setSubmitError(null)
            /*
              Persist the switch's IMPLICIT default before publishing the price.

              Only when the owner never touched it (`visibilityChoice === null`) and the stored
              value disagrees — i.e. the new-listing case, where the row has been showing ON
              while the view still says off. Every explicit flip already wrote through the
              toggle's own handler, so re-sending here would be a duplicate PATCH.

              Without this the step would have shown "Anyone who opens this certificate can see
              the price" and then published a listing the public certificate withholds, which is
              the support ticket this switch exists to prevent.
            */
            if (visibilityChoice === null && isVisible !== isPubliclyVisible) {
              onVisibilityChange(isVisible)
            }
            onSubmit(price, isVisible)
          }}
        >
          {isBusy ? 'Saving…' : isListed ? 'Update price' : 'List for sale'}
        </Button>

        {/*
          Only for an item that IS listed — there is nothing to remove otherwise, and §46 hides an
          action the record's own state makes impossible rather than showing it greyed out.

          A text button rather than a destructive one: unlisting destroys no record, it clears a
          field, and the item can be listed again in two taps. The trash-can treatment would
          overstate a reversible action (§38).
        */}
        {isListed && onRemove && (
          <Button
            fullWidth
            variant='text'
            disabled={isBusy}
            sx={{ marginTop: `${space.sm}px` }}
            onClick={() => {
              setSubmitError(null)
              onRemove?.()
            }}
          >
            {/* "Remove listing", not "Remove from sale": it names the OBJECT the two
                controls on this step share, and it is the same noun as the "Edit listing"
                row that opens the step (§44 — one term per thing, every surface reads it).
                "From sale" named a state instead, which read as a different feature. */}
            {isBusy ? 'Removing…' : 'Remove listing'}
          </Button>
        )}
      </Box>
    </Box>
  )
}

/** The sheet's title adornment while the listing step is open, mirroring `SellSheetP2PMarker`. */
export const ListForSaleMarker = () => (
  <Stack direction='row' alignItems='center' sx={{ gap: `${space.sm}px` }}>
    <Tag size={16} strokeWidth={1.5} />
    <Typography sx={{ fontSize: `${font.label}px`, color: 'text.secondary' }}>Listing</Typography>
  </Stack>
)
